/**
 * CMS API client. Replaces the retired Youbase/EdgeSpark client.
 *
 * - `VITE_CMS_API_URL` → full worker base URL (production).
 * - Empty/undefined → same-origin `/api` (Pages Functions proxy or custom-domain
 *   worker route). In local dev, vite proxies /api to the worker on :8787.
 */
export const CMS_API_BASE: string = (import.meta.env.VITE_CMS_API_URL as string | undefined) ?? '';

export function apiUrl(path: string): string {
  return `${CMS_API_BASE}${path}`;
}

export function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem('gl_admin_token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function jsonFetch(url: string, init?: RequestInit): Promise<any> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${text}`.trim());
  }
  return res.json();
}

/** Published CMS data (public — what the website renders). */
export function fetchPublishedCms(): Promise<any> {
  return jsonFetch(apiUrl(`/api/cms/data?version=published&t=${Date.now()}`));
}

/** Draft CMS data (admin only). */
export function fetchDraftCms(): Promise<any> {
  return jsonFetch(apiUrl(`/api/cms/data?version=draft&t=${Date.now()}`), {
    headers: adminHeaders(),
  });
}

/** Deep-merge a partial payload into the draft. */
export function saveDraft(partial: unknown): Promise<any> {
  return jsonFetch(apiUrl('/api/cms/data'), {
    method: 'POST',
    headers: adminHeaders(),
    body: JSON.stringify(partial),
  });
}

export function publishCms(): Promise<any> {
  return jsonFetch(apiUrl('/api/cms/publish'), {
    method: 'POST',
    headers: adminHeaders(),
  });
}

export function publishStatus(): Promise<any> {
  return jsonFetch(apiUrl('/api/cms/publish/status'), { headers: adminHeaders() });
}

export function resetDraft(): Promise<any> {
  return jsonFetch(apiUrl('/api/cms/reset'), {
    method: 'POST',
    headers: adminHeaders(),
  });
}

export function refreshAiFiles(): Promise<any> {
  return jsonFetch(apiUrl('/api/cms/ai/refresh'), {
    method: 'POST',
    headers: adminHeaders(),
  });
}

export function login(token: string): Promise<any> {
  return jsonFetch(apiUrl('/api/admin/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
}

export interface Inquiry {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  status: 'new' | 'read' | 'replied';
  submittedAt: string;
}

export async function fetchInquiries(): Promise<Inquiry[]> {
  const json = await jsonFetch(apiUrl(`/api/inquiries?t=${Date.now()}`), {
    headers: adminHeaders(),
  });
  return Array.isArray(json?.data) ? json.data : [];
}

export function updateInquiryStatus(id: string, status: Inquiry['status']): Promise<any> {
  return jsonFetch(apiUrl(`/api/inquiries/${id}`), {
    method: 'PUT',
    headers: adminHeaders(),
    body: JSON.stringify({ status }),
  });
}

export function submitInquiry(payload: {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
}): Promise<any> {
  return jsonFetch(apiUrl('/api/inquiries'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function fetchAnalytics(days: number): Promise<any> {
  return jsonFetch(apiUrl(`/api/analytics?days=${days}`), { headers: adminHeaders() });
}
